// Boot-time environment constants. Evaluated once when this module is first imported,
// then frozen for the rest of the page's lifetime. None of these values can change at
// runtime, so plain exported consts beat Recoil atoms here — no subscription overhead,
// usable from any helper (not just hooks/components), single source of truth.

export type MobilePlatform = 'ios' | 'android' | null;

export const mobilePlatform: MobilePlatform = (() => {
  if (typeof window === 'undefined') return null;
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera || '';
  // iOS detection (includes iPhone, iPad, iPod, and iPad on iOS 13+ which reports as Mac)
  if (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) return 'ios';
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return null;
})();

export const isIOS = mobilePlatform === 'ios';
export const isAndroid = mobilePlatform === 'android';

// Which browser the user is in on iOS — affects the path through Settings
// for re-enabling location (Settings → Safari/Chrome/Firefox → Location).
// On non-iOS this is null.
export type IOSBrowser = 'safari' | 'chrome' | 'firefox' | 'edge' | 'other' | null;
export const iosBrowser: IOSBrowser = (() => {
  if (!isIOS) return null;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (/CriOS/.test(ua)) return 'chrome';
  if (/FxiOS/.test(ua)) return 'firefox';
  if (/EdgiOS/.test(ua)) return 'edge';
  if (/Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)) return 'safari';
  return 'other';
})();

export const isPwaInstalled =
  typeof window !== 'undefined' &&
  window.matchMedia('(display-mode: standalone)').matches;

// True when the app is rendered inside an iframe (or when a cross-origin parent
// throws on access — defaulting to true in that case is the safe choice).
export const isInIframe = (() => {
  try { return typeof window !== 'undefined' && window.self !== window.top; }
  catch { return true; }
})();
